import type { EnemyDef } from '../schemas/defs.js';

/**
 * Enemy archetypes (GDD §7).
 *
 * Each one exists to make a specific class answer worth having. If an archetype has no
 * counter, it is a stat check; if it has only one, it is a tax. The comment on each says
 * what it is asking the player for.
 *
 * ⚠ Numbers are PLACEHOLDER — see towers.ts.
 */
export const ENEMIES: EnemyDef[] = [
  {
    id: 'runner',
    name: 'Runner',
    hp: 40,
    armor: 0,
    speed: 1.6,
    bounty: 0,
    leakDamage: 1,
    size: 0.32,
    flags: [],
    sprite: 'enemy_runner',
  },
  {
    // Asks for armor pierce (Arcanist) or %max-HP (Void). Small fast hits bounce.
    id: 'brute',
    name: 'Brute',
    hp: 220,
    armor: 8,
    speed: 0.85,
    bounty: 0,
    leakDamage: 3,
    size: 0.46,
    flags: [],
    sprite: 'enemy_brute',
  },
  {
    // Asks for slows (Frost) or chains (Tempest). Single-target melts one at a time and
    // the other seven walk past.
    id: 'sprinter',
    name: 'Sprinter',
    hp: 18,
    armor: 0,
    speed: 2.8,
    bounty: 0,
    leakDamage: 1,
    size: 0.22,
    flags: [],
    sprite: 'enemy_sprinter',
  },
  {
    // Asks for burst over DPS. A flat per-hit shield punishes fast weak hits specifically.
    id: 'ward',
    name: 'Ward',
    hp: 130,
    armor: 2,
    speed: 1.1,
    bounty: 0,
    leakDamage: 2,
    size: 0.4,
    flags: ['shielded'],
    shieldPerHit: 6,
    sprite: 'enemy_ward',
  },
  {
    // Asks for AoE and spreading damage (Venom, Arcanist detonation).
    id: 'splitter',
    name: 'Splitter',
    hp: 90,
    armor: 1,
    speed: 1.2,
    bounty: 0,
    leakDamage: 2,
    size: 0.4,
    flags: ['splitter'],
    splitInto: { id: 'splitterling', count: 2 },
    sprite: 'enemy_splitter',
  },
  {
    id: 'splitterling',
    name: 'Splitterling',
    hp: 28,
    armor: 0,
    speed: 1.7,
    bounty: 0,
    leakDamage: 1,
    size: 0.24,
    flags: [],
    sprite: 'enemy_splitterling',
  },
  {
    // Asks for execution (Void) or focus-fire targeting. Sustained chip damage stalls out.
    id: 'mender',
    name: 'Mender',
    hp: 70,
    armor: 0,
    speed: 1.15,
    bounty: 0,
    leakDamage: 1,
    size: 0.34,
    flags: ['mender'],
    healRadius: 3,
    healPercentPerSecond: 0.02,
    sprite: 'enemy_mender',
  },
  {
    // Asks for coverage away from the path — punishes hugging one chokepoint.
    id: 'drifter',
    name: 'Drifter',
    hp: 65,
    armor: 0,
    speed: 1.0,
    bounty: 0,
    leakDamage: 2,
    size: 0.34,
    flags: ['flying'],
    sprite: 'enemy_drifter',
  },

  {
    // Asks for cover near the CORE, not near the path (playtest request). Flyers ignore
    // the route entirely and make straight for the Core, and that is the one behaviour
    // that already made a player rearrange mid-run — so there is a fast, cheap one now,
    // early enough to teach the lesson while a board is still cheap to change.
    //
    // Borrows the Drifter's art deliberately: the renderer falls back to a GROUND runner
    // for an unknown key, and a flyer drawn as a walker teaches the wrong thing.
    id: 'shrike',
    name: 'Shrike',
    hp: 55,
    armor: 0,
    speed: 1.9,
    bounty: 0,
    leakDamage: 2,
    size: 0.3,
    flags: ['flying'],
    sprite: 'enemy_drifter',
  },

  // ── Late archetypes (playtest: "the rounds get repetitive") ────────────────────────
  //
  // Every gate above stops at wave 12, so waves 13-28 were a remix of the same seven
  // shapes and the only new thing in the back half of a run was the boss. These carry
  // the mechanics that were previously boss-only — leaping, emerging, blinking, status
  // immunity — down to trash tier, so the second half keeps introducing threats.
  //
  // Same rule as everything above: each asks for a specific answer. Two of them ask it
  // of the classes the damage data says are carrying (Frost lockdown, Tempest stuns),
  // which is the point — a board that solves waves 1-12 should not auto-solve 13-28.
  {
    // Asks for coverage along the PATH, not one killbox. A vaulter clears the stretch
    // your whole board is aimed at and lands past it.
    id: 'vaulter',
    name: 'Vaulter',
    hp: 85,
    armor: 0,
    speed: 1.25,
    bounty: 0,
    leakDamage: 2,
    size: 0.34,
    flags: ['leaper'],
    leapEveryTicks: 150,
    leapTiles: 2.5,
    sprite: 'enemy_vaulter',
  },
  {
    // Asks for damage that lasts. It surfaces PAST the spawn, untouchable on the way up,
    // so a board front-loaded at the entrance never gets its opening volley.
    id: 'hollow',
    name: 'Hollow',
    hp: 150,
    armor: 3,
    speed: 1.0,
    bounty: 0,
    leakDamage: 2,
    size: 0.4,
    flags: [],
    emergeTicks: 90,
    sprite: 'enemy_hollow',
  },
  {
    // Asks for anything that is not a slow. Frost is the strongest opener in the game and
    // a full Frost board simply stops most waves moving; this one walks through it.
    id: 'rimewrought',
    name: 'Rimewrought',
    hp: 200,
    armor: 4,
    speed: 1.15,
    bounty: 0,
    leakDamage: 3,
    size: 0.44,
    flags: [],
    statusImmunities: ['chill', 'freeze'],
    sprite: 'enemy_rimewrought',
  },
  {
    // Asks for anti-air AND target priority at once. The ground Mender at least stands on
    // the path; this one heals the wave from over the scenery.
    id: 'pall-warden',
    name: 'Pall Warden',
    hp: 95,
    armor: 0,
    speed: 1.05,
    bounty: 0,
    leakDamage: 2,
    size: 0.32,
    flags: ['flying', 'mender'],
    healRadius: 3.5,
    healPercentPerSecond: 0.025,
    sprite: 'enemy_pall_warden',
  },
  {
    // Asks for a board that covers both halves, and for a way to kill something that
    // cannot be held. Tempest's answer to everything is a stun; this ignores it.
    id: 'mirrorkin',
    name: 'Mirrorkin',
    hp: 130,
    armor: 2,
    speed: 1.2,
    bounty: 0,
    leakDamage: 2,
    size: 0.36,
    flags: [],
    statusImmunities: ['stun'],
    teleportEveryTicks: 240,
    sprite: 'enemy_mirrorkin',
  },
  {
    // Asks you to still have anti-air at wave 23. A ground tank that becomes two flyers
    // is the one split that changes which towers can even reach the pieces.
    id: 'cystbloom',
    name: 'Cystbloom',
    hp: 170,
    armor: 2,
    speed: 0.95,
    bounty: 0,
    leakDamage: 3,
    size: 0.44,
    flags: ['splitter'],
    splitInto: { id: 'drifter', count: 2 },
    sprite: 'enemy_cystbloom',
  },

  // ── Bosses ─────────────────────────────────────────────────────────────────────────
  // Stat/flag combinations, not bespoke code paths (TRD-06 R-06-16). Each teaches the
  // lesson the next five waves will test.
  {
    id: 'boss-bulwark',
    name: 'Bulwark',
    hp: 1800,
    armor: 22,
    speed: 0.6,
    bounty: 0,
    leakDamage: 4,
    size: 0.7,
    flags: ['boss'],
    sprite: 'boss_bulwark',
  },
  {
    id: 'boss-hive',
    name: 'Hive',
    hp: 2900,
    armor: 6,
    speed: 0.7,
    bounty: 0,
    leakDamage: 4,
    size: 0.7,
    flags: ['boss', 'splitter'],
    splitInto: { id: 'sprinter', count: 6 },
    // Six sprinters are 18 hp each, which against a 2900 hp boss is a rounding error, so
    // the split needs to be sized against the PARENT or it is not a second phase at all.
    // Under 0.45 the brood carries a little under half the fight and arrives at sprinter
    // speed: the AoE check the Hive exists to be, and survivable by a board that has one.
    splitHpFraction: 0.45,
    sprite: 'boss_hive',
  },
  {
    id: 'boss-rust',
    name: 'Warden-of-Rust',
    hp: 4800,
    armor: 8,
    speed: 0.65,
    bounty: 0,
    leakDamage: 5,
    size: 0.75,
    flags: ['boss', 'shielded'],
    shieldPerHit: 24,
    sprite: 'boss_rust',
  },
  {
    id: 'boss-chorus',
    name: 'Chorus',
    hp: 6500,
    armor: 9,
    speed: 0.7,
    bounty: 0,
    leakDamage: 6,
    size: 0.75,
    flags: ['boss', 'mender'],
    healRadius: 6,
    healPercentPerSecond: 0.03,
    sprite: 'boss_chorus',
  },
  {
    /**
     * The finale. Every run ends here (wave 25), so Prism is the one fight the whole
     * game is teaching you to answer.
     *
     * It opens by cracking the floor and rising out of it — `emergeTicks` of untouchable
     * entrance, because a boss deleted during its own reveal is not a boss. Then it
     * crawls: slower than anything else on the board, so the pressure is positional
     * rather than a race. Stun and freeze bounce off (the immunity the comment here has
     * claimed since day one and never had), and every 4 seconds it blinks to the
     * mirrored half of the map — the coin-flip comes off the day's seed, so Prism cheats
     * the same way for everyone playing that daily, and a lane you fortified is not
     * necessarily the lane it finishes in.
     *
     * The fight is a shield cycle (playtest request). Its 3000-point shield is the only
     * thing you can touch while it walks; empty it and Prism drops, freezes for three
     * seconds with its real health exposed, then raises a full bar and walks again.
     *
     * This is why the numbers below look so different from a plain HP pool. Prism only
     * ADVANCES while shielded, so the fight solves itself into a single question — can
     * you break the bar faster than it closes the distance:
     *
     *   tiles flown ≈ hp × shieldHp × speed / (boardDps² × windowSeconds)
     *
     * The square is the whole point. Double your damage and Prism covers a QUARTER the
     * ground, so the difference between a board that holds and one that gets walked over
     * is sharp instead of a slow slide — and a weak board is overrun rather than left in
     * a stalemate it can neither win nor lose.
     *
     * MEASURED with `pnpm --filter @prism/content power-curve prism <tiles>`, not
     * derived: the crossing sits near 1100-1200 board DPS over a 25-tile flight. Prism
     * flies, so that distance is the straight line from its spawn to the Core — which
     * means a Core CLOSE to the spawn is the harder day, since there is less ground for
     * it to lose the race in.
     *
     * That figure is much higher than the 400 this comment used to quote, and the boss did
     * not change: the PROBE was wrong. It spawns Prism into a one-wave world, so the global
     * wave curve every real finale applies was never included, and every crossing point the
     * tool had ever printed was low by the wave-25 scale.
     *
     * Note what losing the race costs, because it is most of the fight's design: a leaked
     * Prism is killed on contact for `leakDamage`, the wave then has no enemies left, and
     * wave 25 clearing is a VICTORY. So a board under the crossing point still finishes the
     * daily rather than losing it — which at the old 8 Core HP made the finale a formality
     * nobody had to fight. At 15 against a 20 hp Core it is a real bill: clean runs pay it
     * and still win, scuffed ones do not.
     */
    id: 'boss-prism',
    name: 'Prism',
    /**
     * AUTHORED hp, which is not the hp of the fight: wave 25 multiplies it by the global
     * health curve, so the real Prism is this times ~35. It came down from 22000 when that
     * curve was made to accelerate — otherwise the finale reaches roughly 780k effective hp
     * and no board of any size kills it, which would put the fight straight back to the
     * formality the leak cost was just raised to remove.
     *
     * Tuned against `power-curve prism 25` to leave the crossing near 1500 board dps: above
     * what the strong probe can reach, below what a genuinely good board manages, so killing
     * the finale stays an achievement rather than a foregone conclusion in either direction.
     * It came DOWN from 22000 when the curve started accelerating, because the same authored
     * number now buys four times the fight.
     */
    hp: 18000,
    armor: 10,
    // Slower than the 0.4 it crawled at before: the shield cycle means it spends whole
    // stretches of the fight not moving at all, and the walk between them should read as
    // inevitability rather than a commute.
    speed: 0.25,
    shieldHp: 3000,
    shieldDownTicks: 300,
    bounty: 0,
    /**
     * 15 against a 20 hp Core, so surviving a leaked finale means having lost at most
     * four hearts across the whole run (owner's call, playtest reports).
     *
     * The old 8 made the finale a formality. Measured, no board under about 1100 board
     * dps kills Prism over a 25-tile flight, so leaking it was the ORDINARY outcome, and
     * contact kills the leaker -- which empties wave 25, which clears the last wave,
     * which wins the run. Nobody was fighting the boss the game builds toward; they were
     * paying 8 hearts to skip it. At 15 the same board still wins if it played clean and
     * loses if it did not, which is the point: the finale asks about the whole run rather
     * than about the last ninety seconds.
     *
     * Not lethal outright, deliberately. An automatic loss would make every wave-24 death
     * feel identical and would throw away the distinction between a board that nearly had
     * it and one that never did.
     */
    leakDamage: 15,
    size: 1.0,
    flags: ['boss', 'flying'],
    statusImmunities: ['stun', 'freeze'],
    emergeTicks: 150,
    teleportEveryTicks: 240,
    sprite: 'boss_prism',
  },

  {
    /**
     * The Disabler (playtest request) — the only enemy that attacks TOWERS.
     *
     * Every 20 seconds it fires a visible line at the strongest tower within 6 tiles, and
     * every tower within 1.8 of that point stops firing for 10. The reach is drawn
     * nowhere: you learn it by losing a board to it once. The SHOT is loud, because a
     * tower going dark with no visible cause reads as a bug rather than as an attack.
     *
     * It exists to punish stacking. Aiming at the strongest tower puts the blast in the
     * middle of whatever was built around it, so a clumped board loses its cluster to one
     * shot while a spread board loses one tower — the lesson buff tiles were supposed to
     * teach, arriving from something that shoots back instead of from a bonus.
     *
     * Fragile on purpose. It has to survive long enough to fire, so killing it before the
     * cooldown comes round is the counter, and that is only possible if something reaches
     * it — which is the second thing a stacked board cannot do.
     */
    id: 'hexwright',
    name: 'Hexwright',
    hp: 380,
    armor: 3,
    speed: 0.75,
    bounty: 0,
    leakDamage: 3,
    size: 0.5,
    flags: [],
    disableRange: 6,
    disableRadius: 1.8,
    disableTicks: 600,
    disableEveryTicks: 1200,
    sprite: 'enemy_drifter',
  },

  {
    /**
     * The Aegis (playtest request) — a plate across its front, and nothing anywhere else.
     *
     * A shot arriving within 60 of dead ahead lands for a quarter of its damage. A shot
     * from the side or from behind lands in full, so the lesson is specific and positional:
     * do not line your towers up at the end of a corridor staring down the column. Put
     * them BESIDE the lane.
     *
     * The arc is narrow for a measured reason. A wide plate averages out — an enemy
     * walking past a tower spends about half its time in that circle approaching and half
     * departing — so it would read as "tougher" and teach nothing at all.
     *
     * Poison goes straight through: sourceless damage has no direction to arrive from, so
     * the plate cannot turn it. An Aegis you cannot get behind is one you can still rot,
     * and that is the second answer rather than an oversight.
     */
    id: 'aegis',
    name: 'Aegis',
    hp: 240,
    armor: 2,
    speed: 0.95,
    bounty: 0,
    leakDamage: 3,
    size: 0.5,
    flags: [],
    // cos 60 — the plate covers 60 either side of straight ahead.
    frontalCos: 0.5,
    frontalReduction: 0.75,
    sprite: 'enemy_brute',
  },

  // ── The B-20 expansion — five more, so the per-seed draw has real variety ──────────
  // Still stat/flag combinations only (R-06-16). Each is one existing mechanic worn
  // boss-sized; a boss drawn past its gate scales +10% HP per wave in the generator.
  {
    // Asks for slows and reach — low HP for a boss, but it is at your Core in half the time.
    id: 'boss-comet',
    name: 'Comet',
    hp: 1500,
    armor: 2,
    speed: 1.5,
    bounty: 0,
    leakDamage: 4,
    size: 0.6,
    flags: ['boss'],
    sprite: 'boss_comet',
  },
  {
    // B-16's bouncer. Every 2.5s it hops 2.5 tiles of path — freezing or stunning it
    // grounds the hop, and everything else has to shoot the moving stretch between them.
    id: 'boss-bounder',
    name: 'Bounder',
    hp: 3200,
    armor: 4,
    speed: 0.55,
    bounty: 0,
    leakDamage: 5,
    size: 0.7,
    flags: ['boss', 'leaper'],
    leapEveryTicks: 150,
    leapTiles: 2.5,
    sprite: 'boss_bounder',
  },
  {
    // Two-stage cascade: Matriarch → 3 Splitters → 6 Splitterlings. Peak AoE check.
    id: 'boss-matriarch',
    name: 'Matriarch',
    hp: 3800,
    armor: 5,
    speed: 0.6,
    bounty: 0,
    leakDamage: 5,
    size: 0.75,
    flags: ['boss', 'splitter'],
    splitInto: { id: 'splitter', count: 3 },
    // Sizes the FIRST rank only. Those three are ordinary Splitters from there on, so
    // their own children come out at the usual ~60% and the cascade lands near 65% of the
    // Matriarch in total — deliberately the largest split in the game, since a two-stage
    // break is the whole reason this boss exists.
    splitHpFraction: 0.4,
    sprite: 'boss_matriarch',
  },
  {
    // The wall: heavy flat armor AND a per-hit shield. Only pierce, true damage or big
    // slow hits get through — a rapid-fire board watches its numbers evaporate.
    id: 'boss-monolith',
    name: 'Monolith',
    hp: 4200,
    armor: 12,
    speed: 0.5,
    bounty: 0,
    leakDamage: 6,
    size: 0.8,
    flags: ['boss', 'shielded'],
    shieldPerHit: 14,
    sprite: 'boss_monolith',
  },
  {
    // A flying Chorus: drifts straight for the Core, mending itself and its escort.
    id: 'boss-aurora',
    name: 'Aurora',
    hp: 6800,
    armor: 6,
    speed: 0.65,
    bounty: 0,
    leakDamage: 7,
    size: 0.8,
    flags: ['boss', 'flying', 'mender'],
    healRadius: 5,
    healPercentPerSecond: 0.025,
    sprite: 'boss_aurora',
  },
];
